import { setup, assign, fromPromise } from "xstate"
import type { OwnerOutput } from "@core/composables"
import type { Address } from "@types"
import type { BatiService } from "@core/services/bati.service"
import type {
    EnedisService,
    SearchPdlResult,
    VerifyPdlResult,
} from "@core/services/enedis.service"
import type { GeocodeResponse } from "@core/services/bati.service"
import { pdlValidator } from "@core/helpers"

// ================================
// TYPES
// ================================

export type PdlSearchMachineInput = {
    address?: Address | null
    owner?: OwnerOutput
    batiService: BatiService
    enedisService: EnedisService
}

export type PdlSearchContext = {
    address?: Address | null
    owner?: OwnerOutput
    pdlId: string
    searchResult: SearchPdlResult | null
    contractName?: string
    verifyResult?: VerifyPdlResult | false
}

export type PdlSearchEvent =
    | { type: "START" }
    | { type: "INPUT_PDL"; value: string }
    | { type: "VERIFY" }
    | { type: "RETRY" }

// ================================
// CONSTANTS
// ================================

/** Longueur attendue du PDL / idsource (PRM Linky) côté métier et API */
export const PDL_IDSOURCE_CHAR_LENGTH = 14

/** PDL saisi : espaces ignorés pour validation et appel API */
function normalizePdlIdsource(value: string | undefined): string {
    return (value ?? "").trim().replace(/\s+/g, "")
}

function isValidPdlIdsource(value: string | undefined): boolean {
    return pdlValidator(normalizePdlIdsource(value))
}

// ================================
// HELPERS
// ================================

function formatAddressForGeocode(addr: Address | null | undefined): string {
    if (!addr) return ""
    const parts = [addr.street, addr.zipcode, addr.city].filter(Boolean)
    return parts.join(" ")
}

function hasCitycode(addr: Address | null | undefined): boolean {
    return !!addr?.citycode?.trim()
}

function getSearchParams(
    address: Address | null | undefined,
    _owner?: OwnerOutput
): { street: string; zipcode: string; citycode: string } | null {
    if (!address?.street?.trim() || !address?.zipcode?.trim()) return null
    const citycode =
        address.citycode?.trim() ||
        (address as Address & { citycode?: string }).citycode ||
        ""
    if (!citycode) return null
    return {
        street: address.street.trim(),
        zipcode: address.zipcode.trim(),
        citycode,
    }
}

// ================================
// MACHINE
// ================================

/**
 * Machine de recherche et vérification PDL : géocodage (si pas de citycode), recherche Enedis, puis vérification en base (api-metier).
 * Utilisée par PdlFormSearch.vue.
 */
export function createPdlSearchMachine(input: PdlSearchMachineInput) {
    const { batiService, enedisService } = input

    return setup({
        types: {} as {
            context: PdlSearchContext
            events: PdlSearchEvent
        },
        actors: {
            geocode: fromPromise(
                async ({
                    input: actorInput,
                }: {
                    input: { addressStr: string }
                }) => batiService.geocode(actorInput.addressStr)
            ),
            searchPdl: fromPromise(
                async ({
                    input: actorInput,
                }: {
                    input: {
                        address: Address | null | undefined
                        owner: OwnerOutput | undefined
                    }
                }) => {
                    const params = getSearchParams(
                        actorInput.address,
                        actorInput.owner
                    )
                    if (!params) return null
                    const owner = actorInput.owner
                    if (owner?.company != null) {
                        const siret = owner.company.idcompany?.trim()
                        return enedisService.searchPdlEntreprise({
                            ...params,
                            name: owner.company.name ?? "",
                            ...(siret ? { siret } : {}),
                        })
                    }
                    return enedisService.searchPdlParticulier({
                        ...params,
                        lastname: owner?.user?.lastname ?? "",
                    })
                }
            ),
            verifyPdl: fromPromise(
                async ({
                    input: actorInput,
                }: {
                    input: { idsource: string }
                }) => enedisService.verifyPdl({ idsource: actorInput.idsource })
            ),
        },
        actions: {
            setPdlId: assign({
                pdlId: ({ context, event }) =>
                    event.type === "INPUT_PDL" ? event.value : context.pdlId,
            }),
            assignGeocodeResult: assign({
                address: ({ context, event }) => {
                    const out = (
                        event as unknown as { output: GeocodeResponse }
                    ).output
                    const addr = context.address
                    if (!addr) return addr
                    return {
                        ...addr,
                        citycode: out?.citycode ?? addr.citycode,
                    }
                },
            }),
            assignSearchResult: assign({
                searchResult: ({ event }) =>
                    (event as unknown as { output: SearchPdlResult | null })
                        .output ?? null,
                pdlId: ({ context, event }) => {
                    const res = (
                        event as unknown as {
                            output: SearchPdlResult | null
                        }
                    ).output
                    return res?.pdl ?? context.pdlId
                },
                contractName: ({ event }) => {
                    const res = (
                        event as unknown as {
                            output: SearchPdlResult | null
                        }
                    ).output
                    return (res as SearchPdlResult & { name?: string })?.name
                },
            }),
            assignVerifyResult: assign({
                verifyResult: ({ event }) =>
                    (event as unknown as { output: VerifyPdlResult | false })
                        .output,
            }),
        },
        guards: {
            hasCitycode: ({ context }) => hasCitycode(context.address),
            hasNoCitycode: ({ context }) => !hasCitycode(context.address),
            hasPdlId: ({ context }) => isValidPdlIdsource(context.pdlId),
            hasSearchResult: ({ event }) =>
                event != null &&
                (event as unknown as { output?: unknown }).output != null,
        },
    }).createMachine({
        id: "pdl-search",
        context: {
            address: input.address ?? undefined,
            owner: input.owner,
            pdlId: "",
            searchResult: null,
            contractName: undefined,
            verifyResult: undefined,
        } as PdlSearchContext,
        initial: "idle",
        states: {
            idle: {
                on: {
                    START: [
                        {
                            target: "geocoding",
                            guard: "hasNoCitycode",
                        },
                        {
                            target: "searching",
                            guard: "hasCitycode",
                        },
                    ],
                    INPUT_PDL: { actions: ["setPdlId"] },
                },
            },
            geocoding: {
                invoke: {
                    id: "geocoding",
                    src: "geocode",
                    input: ({ context }: { context: PdlSearchContext }) => ({
                        addressStr: formatAddressForGeocode(context.address),
                    }),
                    onDone: {
                        actions: ["assignGeocodeResult"],
                        target: "searching",
                    },
                    onError: { target: "searchDoneWithoutResult" },
                },
                on: { INPUT_PDL: { actions: ["setPdlId"] } },
            },
            searching: {
                invoke: {
                    id: "searching",
                    src: "searchPdl",
                    input: ({ context }: { context: PdlSearchContext }) => ({
                        address: context.address,
                        owner: context.owner,
                    }),
                    onDone: [
                        {
                            target: "searchDoneWithResult",
                            guard: "hasSearchResult",
                            actions: ["assignSearchResult"],
                        },
                        { target: "searchDoneWithoutResult" },
                    ],
                    onError: { target: "searchDoneWithoutResult" },
                },
                on: { INPUT_PDL: { actions: ["setPdlId"] } },
            },
            searchDoneWithResult: {
                on: {
                    VERIFY: {
                        target: "verifying",
                        guard: "hasPdlId",
                    },
                    INPUT_PDL: { actions: ["setPdlId"] },
                },
            },
            searchDoneWithoutResult: {
                on: {
                    VERIFY: {
                        target: "verifying",
                        guard: "hasPdlId",
                    },
                    INPUT_PDL: { actions: ["setPdlId"] },
                },
            },
            verifying: {
                invoke: {
                    id: "verifying",
                    src: "verifyPdl",
                    input: ({ context }: { context: PdlSearchContext }) => ({
                        idsource: normalizePdlIdsource(context.pdlId),
                    }),
                    onDone: {
                        actions: ["assignVerifyResult"],
                        target: "done",
                    },
                    onError: { target: "error" },
                },
            },
            error: {
                on: {
                    RETRY: { target: "verifying", guard: "hasPdlId" },
                },
            },
            done: {
                type: "final",
            },
        },
    })
}

export type PdlSearchMachine = ReturnType<typeof createPdlSearchMachine>
